import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {SECTION_IDS} from '../utils/constants';
import {trackMenuInteraction} from '../utils/analytics';
import { fetchPastaTypes, fetchSauces, fetchDrinks } from '../services/firestoreService';

const Menu = () => {
    const {t} = useTranslation();
    const [activeMenuTab, setActiveMenuTab] = useState('pasta');
    const [pastaTypes, setPastaTypes] = useState([]);
    const [sauces, setSauces] = useState([]);
    const [drinks, setDrinks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [pastaTypesData, saucesData, drinksData] = await Promise.all([
                    fetchPastaTypes(),
                    fetchSauces(),
                    fetchDrinks()
                ]);

                setPastaTypes(pastaTypesData);
                setSauces(saucesData);
                setDrinks(drinksData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Track initial menu tab view
    useEffect(() => {
        trackMenuInteraction('Menu', activeMenuTab, 'View');
    }, []);

    // Handle tab change and track it
    const handleTabChange = (tabKey) => {
        trackMenuInteraction('Menu', tabKey, 'Select');
        setActiveMenuTab(tabKey);
    };


    if (loading) {
        return (
            <section id={SECTION_IDS.MENU} className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('menu.title')}</h2>
                    <div className="flex justify-center">
                        <p>Caricamento...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id={SECTION_IDS.MENU} className="py-16 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">{t('menu.title')}</h2>

                {/* Mobile Tab Navigation */}
                <div className="md:hidden mb-6">
                    <div className="flex overflow-x-auto space-x-2 pb-2">
                        {[
                            {key: 'pasta', label: `🍝 ${t('menu.pasta.title')}`},
                            {key: 'sauces', label: `🍅 ${t('menu.sauces.title')}`},
                            {key: 'drinks', label: `🥤 ${t('menu.drinks.title')}`}
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => handleTabChange(tab.key)}
                                className={`flex-shrink-0 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                                    activeMenuTab === tab.key
                                        ? 'bg-orange-100 text-orange-800 font-semibold border-b-2 border-orange-600'
                                        : 'text-gray-700 bg-gray-50 hover:bg-gray-100'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col md:flex-row bg-gray-50 rounded-lg overflow-hidden">
                    {/* Desktop Tab Navigation */}
                    <div className="hidden md:block w-48 lg:w-64 bg-white border-r border-gray-200">
                        <div className="p-4">
                            {[
                                {key: 'pasta', label: `🍝 ${t('menu.pasta.title')}`},
                                {key: 'sauces', label: `🍅 ${t('menu.sauces.title')}`},
                                {key: 'drinks', label: `🥤 ${t('menu.drinks.title')}`}
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => handleTabChange(tab.key)}
                                    className={`w-full text-left p-3 lg:p-4 rounded-lg mb-2 transition-colors text-sm lg:text-base ${
                                        activeMenuTab === tab.key
                                            ? 'bg-orange-100 text-orange-800 font-semibold border-l-4 border-orange-600'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 p-4 md:p-6 lg:p-8">
                        {activeMenuTab === 'pasta' && (
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('menu.pasta.title')}</h3>
                                <p className="text-gray-600 mb-8">
                                    {t('menu.pasta.description')}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {pastaTypes.map((pasta, index) => (
                                        <div
                                            key={pasta.id}
                                            className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-start mb-2">
                                                {pasta.imageUrl && (
                                                    <div className="mr-4 flex-shrink-0">
                                                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-orange-200 flex items-center justify-center bg-white">
                                                            <img
                                                                src={pasta.imageUrl}
                                                                alt={t(`${pasta.translationKey}.label`)}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <h4 className="font-semibold text-lg text-gray-900">
                                                            {t(`${pasta.translationKey}.label`)}
                                                        </h4>
                                                        <span className="text-orange-600 font-bold text-lg">
                                                            €{pasta.pricePerUnit?.toFixed(2)} / hg
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600 text-sm mt-1">
                                                        {t(`${pasta.translationKey}.description`)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeMenuTab === 'sauces' && (
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('menu.sauces.title')}</h3>
                                <p className="text-gray-600 mb-8">{t('menu.sauces.description')}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {sauces.map((sauce) => (
                                        <div
                                            key={sauce.id}
                                            className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-start mb-2">
                                                {sauce.imageUrl && (
                                                    <div className="mr-4 flex-shrink-0">
                                                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-orange-200 flex items-center justify-center bg-white">
                                                            <img
                                                                src={sauce.imageUrl}
                                                                alt={t(`${sauce.translationKey}.label`)}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <h4 className="font-semibold text-lg text-gray-900">
                                                            {t(`${sauce.translationKey}.label`)}
                                                        </h4>
                                                        <span className="text-orange-600 font-bold text-lg">
                                                            €{sauce.price?.toFixed(2)}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-600 text-sm mt-1">
                                                        {t(`${sauce.translationKey}.description`)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeMenuTab === 'drinks' && (
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('menu.drinks.title')}</h3>
                                <p className="text-gray-600 mb-8">{t('menu.drinks.description')}</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {drinks.map((drink) => (
                                        <div
                                            key={drink.id}
                                            className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-start mb-2">
                                                {drink.imageUrl && (
                                                    <div className="mr-4 flex-shrink-0">
                                                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-orange-200 flex items-center justify-center bg-white">
                                                            <img
                                                                src={drink.imageUrl}
                                                                alt={t(`${drink.translationKey}.label`)}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <h4 className="font-semibold text-lg text-gray-900">
                                                            {t(`${drink.translationKey}.label`)}
                                                        </h4>
                                                        <span className="text-orange-600 font-bold text-lg">
                                                            €{drink.price?.toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Menu;
